import { useEffect, useState } from "react";
import { CalendarClock, Send, Loader2, MailCheck, History, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDateTime } from "../../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function DirectionReportSchedule() {
  const [cfg, setCfg] = useState(null);
  const [sending, setSending] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get("/admin/reports/schedule/history").then(({ data }) => setHistory(data.items || [])).catch(() => {});
  useEffect(() => { api.get("/admin/reports/schedule").then(({ data }) => setCfg(data)).catch((e) => toast.error(apiError(e))); loadHistory(); }, []);

  const save = async (patch) => {
    const next = { ...cfg, ...patch }; setCfg(next);
    try { await api.put("/admin/reports/schedule", { enabled: next.enabled, emails: next.emails || "", frequency: next.frequency || "mensuel" }); toast.success("Planification enregistrée"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const sendNow = async () => {
    setSending(true);
    try { const { data } = await api.post("/admin/reports/schedule/send-now"); if (data.sent) toast.success(`Rapport envoyé à ${data.sent} destinataire(s) · ${data.period}`); else toast.warning(data.error || "Aucun destinataire configuré"); loadHistory(); }
    catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };
  const sendTest = async () => {
    if (!testEmail.trim()) { toast.error("Saisissez une adresse email de test"); return; }
    setTesting(true);
    try { const { data } = await api.post("/admin/reports/schedule/send-test", { email: testEmail.trim() }); if (data.sent) toast.success(`Aperçu envoyé à ${testEmail.trim()} · ${data.period}`); else toast.warning(data.error || "Envoi impossible"); loadHistory(); }
    catch (e) { toast.error(apiError(e)); } finally { setTesting(false); }
  };

  if (!cfg) return null;
  return (
    <div className="rounded-2xl border border-navy/15 bg-navy/[0.03] p-4" data-testid="direction-report-schedule">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <CalendarClock size={16} className="text-navy" />
        <h3 className="font-display text-sm font-semibold text-navy">Rapport direction programmé (email)</h3>
        <label className="ml-auto inline-flex items-center gap-1.5 text-xs text-slate-600">
          <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => save({ enabled: e.target.checked })} className="h-4 w-4 accent-navy" data-testid="report-sched-enabled" />
          Activer l'envoi automatique
        </label>
      </div>
      <p className="mb-3 text-[11px] text-slate-500">
        Envoie automatiquement le rapport combiné (Résultat des soins · Ambulances · Satisfaction) aux responsables, avec récapitulatif et lien Google Drive.
      </p>
      <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
        <div>
          <label className="mb-1 block text-[11px] text-slate-500">Destinataires (emails séparés par des virgules)</label>
          <Input value={cfg.emails || ""} onChange={(e) => setCfg((c) => ({ ...c, emails: e.target.value }))} onBlur={(e) => save({ emails: e.target.value })}
            placeholder="direction@exemple.io, dg@exemple.io" className="h-9 text-xs" data-testid="report-sched-emails" />
        </div>
        <div>
          <label className="mb-1 block text-[11px] text-slate-500">Fréquence</label>
          <select value={cfg.frequency || "mensuel"} onChange={(e) => save({ frequency: e.target.value })} data-testid="report-sched-frequency"
            className="h-9 rounded-lg border border-slate-200 px-2 text-xs">
            <option value="hebdomadaire">Hebdomadaire</option>
            <option value="mensuel">Mensuel</option>
            <option value="trimestriel">Trimestriel</option>
          </select>
        </div>
        <Button size="sm" onClick={sendNow} disabled={sending} data-testid="report-sched-send-now" className="h-9 rounded-full bg-navy px-4 text-xs text-white hover:bg-navy-light">
          {sending ? <Loader2 size={14} className="animate-spin" /> : <><Send size={13} className="mr-1" />Envoyer maintenant</>}
        </Button>
      </div>
      {cfg.last_sent && <p className="mt-3 text-[11px] text-emerald-600" data-testid="report-sched-last">Dernier envoi : {fmtDateTime(cfg.last_sent)} {cfg.last_period ? `(période ${cfg.last_period})` : ""}</p>}
      <div className="mt-4 border-t border-slate-200 pt-3">
        <label className="mb-1 block text-[11px] text-slate-500">Aperçu de test — s'envoyer le rapport avant l'envoi groupé</label>
        <div className="flex items-center gap-2">
          <Input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="votre@email.io" className="h-9 flex-1 text-xs" data-testid="report-sched-test-email" />
          <Button size="sm" variant="outline" onClick={sendTest} disabled={testing} data-testid="report-sched-send-test" className="h-9 rounded-full px-4 text-xs">
            {testing ? <Loader2 size={14} className="animate-spin" /> : <><MailCheck size={13} className="mr-1" />Envoyer un test</>}
          </Button>
        </div>
      </div>
      {history.length > 0 && (
        <div className="mt-4 border-t border-slate-200 pt-3" data-testid="report-sched-history">
          <div className="mb-2 flex items-center gap-2 text-[11px] font-medium text-slate-500"><History size={13} />Historique des envois</div>
          <div className="max-h-52 space-y-1.5 overflow-auto pr-1">
            {history.map((h, i) => {
              const src = { manuel: ["Manuel", "bg-navy"], test: ["Test", "bg-slate-400"], auto: ["Auto", "bg-emerald-500"] }[h.source] || ["Manuel", "bg-navy"];
              const inner = (
                <>
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium text-white ${src[1]}`}>{src[0]}</span>
                  <span className="font-medium text-navy">{h.period}</span>
                  <span className="text-slate-500">· {h.sent} envoi(s)</span>
                  <span className="truncate text-slate-400" title={(h.recipients || []).join(", ")}>· {(h.recipients || []).join(", ")}</span>
                  {h.drive_link && <ExternalLink size={12} className="shrink-0 text-ciel" />}
                  <span className="ml-auto shrink-0 text-slate-400">{fmtDateTime(h.created_at)}</span>
                </>
              );
              return h.drive_link
                ? <a key={i} href={h.drive_link} target="_blank" rel="noreferrer" title="Ouvrir le PDF (Google Drive)" data-testid="report-sched-history-item" className="flex items-center gap-2 rounded-lg border border-ciel/30 bg-ciel/5 px-2.5 py-1.5 text-[11px] transition-colors hover:bg-ciel/10">{inner}</a>
                : <div key={i} title="PDF non archivé (Google Drive non connecté)" data-testid="report-sched-history-item" className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50 px-2.5 py-1.5 text-[11px]">{inner}</div>;
            })}
          </div>
        </div>
      )}
    </div>
  );
}
